import React, { ReactNode } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { SkeletonListRow } from '@/components/ui/skeleton';
import { cn } from '@/utils/cn';

export interface HomeSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  icon: React.ComponentProps<typeof Ionicons>['name'];
  title: string;
  subtitle?: string;
  /** Texto do rodapé que leva para a página completa. */
  seeMoreLabel?: string;
  onSeeMore: () => void;
  children: ReactNode;
}

/**
 * Prévia de uma página dentro de um diálogo, aberta a partir de um card da Home.
 * Cabeçalho + corpo rolável + rodapé "Ver mais" que fecha e navega.
 * O conteúdo só monta quando `open` — assim as queries do resumo não rodam à toa.
 */
export function HomeSummaryDialog({
  open,
  onClose,
  icon,
  title,
  subtitle,
  seeMoreLabel = 'Ver mais',
  onSeeMore,
  children,
}: HomeSummaryDialogProps) {
  const handleSeeMore = () => {
    onClose();
    onSeeMore();
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md gap-4 rounded-2xl p-0">
        <View className="flex-row items-center gap-3 px-4 pt-4">
          <View
            className="h-10 w-10 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary"
            style={{ borderCurve: 'continuous' }}
          >
            <Ionicons className="text-primary dark:text-white" name={icon} size={18} />
          </View>
          <View className="flex-1 gap-0.5">
            <ThemedText className="font-bold text-base text-card-foreground">{title}</ThemedText>
            {subtitle && (
              <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
                {subtitle}
              </ThemedText>
            )}
          </View>
          <Pressable
            onPress={onClose}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Fechar"
            className="h-9 w-9 items-center justify-center rounded-full bg-neutral-100 active:opacity-70"
          >
            <Ionicons className="text-neutral-600" name="close" size={18} />
          </Pressable>
        </View>

        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={{ maxHeight: 380 }}
          contentContainerStyle={{ paddingHorizontal: 16, gap: 10 }}
          showsVerticalScrollIndicator={false}
        >
          {open ? children : null}
        </ScrollView>

        <Pressable
          onPress={handleSeeMore}
          accessibilityRole="button"
          className="flex-row items-center justify-center gap-1.5 border-t border-neutral-900/5 px-4 py-3.5 active:opacity-70"
        >
          <ThemedText className="font-semibold text-sm text-primary">{seeMoreLabel}</ThemedText>
          <Ionicons name="arrow-forward" size={15} color="#FD5001" />
        </Pressable>
      </DialogContent>
    </Dialog>
  );
}

/** Linha de destaque (rótulo + valor) usada no topo dos resumos. */
export function SummaryStat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'positive' | 'negative' | 'muted';
}) {
  const toneClass =
    tone === 'positive'
      ? 'text-success-600'
      : tone === 'negative'
        ? 'text-error-600'
        : tone === 'muted'
          ? 'text-neutral-500'
          : 'text-card-foreground';

  return (
    <View
      className="flex-1 gap-0.5 rounded-2xl bg-neutral-100 p-3"
      style={{ borderCurve: 'continuous' }}
    >
      <ThemedText className="font-medium text-xs text-neutral-500" numberOfLines={1}>
        {label}
      </ThemedText>
      <ThemedText
        className={cn('font-bold text-base', toneClass)}
        style={{ fontVariant: ['tabular-nums'] }}
        numberOfLines={1}
        adjustsFontSizeToFit
      >
        {value}
      </ThemedText>
    </View>
  );
}

/** Item de lista do resumo: título, descrição e valor/etiqueta à direita. */
export function SummaryRow({
  icon,
  title,
  description,
  value,
  valueTone = 'default',
  onPress,
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  title: string;
  description?: string;
  value?: string;
  valueTone?: 'default' | 'positive' | 'negative' | 'muted';
  onPress?: () => void;
}) {
  const valueClass =
    valueTone === 'positive'
      ? 'text-success-600'
      : valueTone === 'negative'
        ? 'text-error-600'
        : valueTone === 'muted'
          ? 'text-neutral-500'
          : 'text-card-foreground';

  const content = (
    <>
      <View className="h-9 w-9 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary">
        <Ionicons className="text-primary dark:text-white" name={icon} size={15} />
      </View>
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-sm text-card-foreground" numberOfLines={1}>
          {title}
        </ThemedText>
        {description && (
          <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
            {description}
          </ThemedText>
        )}
      </View>
      {value && (
        <ThemedText
          className={cn('font-semibold text-sm', valueClass)}
          style={{ fontVariant: ['tabular-nums'] }}
          numberOfLines={1}
        >
          {value}
        </ThemedText>
      )}
    </>
  );

  const className =
    'flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 px-3 py-2.5';

  if (!onPress) {
    return (
      <View className={className} style={{ borderCurve: 'continuous' }}>
        {content}
      </View>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      className={cn(className, 'active:opacity-70')}
      style={{ borderCurve: 'continuous' }}
    >
      {content}
    </Pressable>
  );
}

export function SummarySkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <View>
      {Array.from({ length: rows }).map((_, index) => (
        <SkeletonListRow key={index} />
      ))}
    </View>
  );
}

export function SummaryEmpty({ message }: { message: string }) {
  return (
    <View className="items-center gap-1 py-6">
      <Ionicons className="text-neutral-400" name="file-tray-outline" size={22} />
      <ThemedText className="text-center text-sm text-neutral-500">{message}</ThemedText>
    </View>
  );
}
